import { ApiNS } from 'app/api/api';
import { apiGetInspectionEvents, apiSaveInspectionEventsOrder } from 'app/api/dcra-inspections/inspection-events/inspection-events';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import { AppThunk } from './index';

export interface InspectionEventsState {
  errorMessage: string;
  isEventsLoading: boolean;
  isEventsLoadingError: string;
  eventsCollection: AppNS.InspectionEvent[];
  isEventsOrderLoading: boolean;
  isEventsOrderLoadingError: string;
  currentDayId: string;
  currentDayState: AppNS.InspectionEventDayState;
}

const initialState: InspectionEventsState = {
  errorMessage: '',
  isEventsLoadingError: '',
  isEventsLoading: false,
  isEventsOrderLoading: false,
  isEventsOrderLoadingError: '',
  eventsCollection: [],
  currentDayId: '',
  currentDayState: 'pending',
};

const inspectionEvents = createSlice({
  name: 'inspectionEvents',
  initialState,
  reducers: {
    getEventsStart(state: InspectionEventsState) {
      state.isEventsLoading = true;
    },
    getEventsSuccess(
      state: InspectionEventsState,
      action: PayloadAction<ApiNS.GetInspectionEventsApiResponse>
    ) {
      state.isEventsLoading = false;
      state.isEventsLoadingError = '';
      state.eventsCollection = action.payload.inspections;
      state.currentDayId = action.payload.id;
      state.currentDayState = action.payload.state;
    },
    getEventsFailed(
      state: InspectionEventsState,
      action: PayloadAction<string>
    ) {
      state.isEventsLoading = false;
      state.isEventsLoadingError = action.payload;
    },
    setEventsOrderStart(state: InspectionEventsState) {
      state.isEventsOrderLoading = true;
    },
    setEventsOrderSuccess(
      state: InspectionEventsState,
      action: PayloadAction<ApiNS.GetInspectionEventsApiResponse>
    ) {
      state.isEventsOrderLoading = false;
      state.isEventsOrderLoadingError = '';
      state.eventsCollection = state.eventsCollection.map(
        (ev: AppNS.InspectionEvent) => {
          const changed = action.payload.inspections.find(
            (incomingEv: AppNS.InspectionEvent) => ev.id === incomingEv.id
          );
          return changed || ev;
        }
      );
      state.currentDayState = action.payload.state;
    },
    setEventsOrderFailed(
      state: InspectionEventsState,
      action: PayloadAction<string>
    ) {
      state.isEventsOrderLoading = false;
      state.isEventsOrderLoadingError = action.payload;
    },
    resetErrors(
      state: InspectionEventsState,
    ) {
      state.isEventsLoadingError = '';
      state.isEventsOrderLoadingError = '';
    }
  },
});

export const {
  getEventsStart,
  getEventsSuccess,
  getEventsFailed,
  setEventsOrderStart,
  setEventsOrderSuccess,
  setEventsOrderFailed,
  resetErrors,
} = inspectionEvents.actions;

export default inspectionEvents.reducer;

export const getEvents =
  (params: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getEventsStart());
      const result = await apiGetInspectionEvents(params);
      dispatch(getEventsSuccess(result));
    } catch (message) {
      if (message) {
        dispatch(getEventsFailed(message as string));
      }
    }
  };

export const setEventsOrder =
  (id: string, inspections: AppNS.InspectionEventShort[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setEventsOrderStart());
      const result = await apiSaveInspectionEventsOrder(id, inspections);
      dispatch(setEventsOrderSuccess(result));
    } catch (message) {
      if (message) {
        dispatch(setEventsOrderFailed(message as string));
      }
    }
  };
